<template>
    <div>
        <Drawer
            v-if="selectedWebTxn"
            :drawerStatus="webRequestViewDrawerStatus"
            bgColor='#E3F2FD'
            headerBgColor="#E3F2FD"
            drawerWidth='85%'
            zIndex=100
            :title="selectedWebTxn.uri"
            :subtitle="toLongFormat(selectedWebTxn.start_time)"
            v-on:onDismiss="webRequestViewDrawerStatus = 'collapsed'"
            v-on:onOverlayClicked="webRequestViewDrawerStatus = 'collapsed'">
            <WebRequestView
                class="px-5"
                :selectedWebTxn="selectedWebTxn"
                v-on:onBlockchainTxnSelected="onBlockchainTxnSelected">
            </WebRequestView>
        </Drawer>

        <Drawer
            v-if="selectedBlockchainTxn"
            :drawerStatus="blockchainRequestViewDrawerStatus"
            bgColor='#BBDEFB'
            headerBgColor="#BBDEFB"
            drawerWidth='69%'
            zIndex=200
            :title="selectedBlockchainTxn.chaincode_name + '::' + selectedBlockchainTxn.chaincode_function"
            :subtitle="toLongFormat(selectedBlockchainTxn.start_time)"
            v-on:onDismiss="blockchainRequestViewDrawerStatus = 'collapsed'"
            v-on:onOverlayClicked="webRequestViewDrawerStatus = 'collapsed'; blockchainRequestViewDrawerStatus = 'collapsed'">
            <BlockchainRequestView
                class="px-5"
                :selectedBlockchainTxn="selectedBlockchainTxn"
                v-on:onDismissView="webRequestViewDrawerStatus = 'collapsed'; blockchainRequestViewDrawerStatus = 'collapsed'">
            </BlockchainRequestView>
        </Drawer>

        <v-row class="mt-4" justify="space-between">
            <v-col cols="10">
                <h2 class="text-uppercase">
                    <WebAppSelection></WebAppSelection>
                    <span class="ml-4 text-subtitle-1 font-weight-bold">{{ getFriendlySelectedTimePeriodStart }} - {{ getFriendlySelectedTimePeriodEnd }}</span>
                    <span class="mr-5">
                        <TimePeriodSelection
                            buttonText="Last"
                            :defaultSelection="getTimeSelection"
                            v-on:onTimePeriodSelected="onTimePeriodSelected"/>
                    </span>
                    <TimePeriodSelection
                        buttonText="Contrast to"
                        :defaultSelection="getContrastTimeSelection"
                        v-on:onTimePeriodSelected="onContrastTimePeriodSelected"/>
                </h2>
            </v-col>
            <v-col cols="2">
                <AutoRefresh class="mt-n2" v-on:onRefresh="onRefresh"/>
            </v-col>
        </v-row>

        <v-row>
            <v-col>
                <span class="pl-2 text-h6 text-uppercase">Recent Transactions</span>
            </v-col>
        </v-row>

        <v-row no-gutters>
            <v-col>
                <v-card>
                    <v-card-title>
                        <v-row no-gutters>
                            <v-col cols="4">
                                <v-text-field
                                    clearable
                                    v-model="search"
                                    append-icon="mdi-magnify"
                                    label="Search by transaction name"
                                    single-line
                                    hide-details
                                    @input="onSearchChange"
                                    @click:clear="onClearSearch"
                                    @keydown.enter="onSearch">
                                </v-text-field>
                            </v-col>
                        </v-row>
                    </v-card-title>
                    <v-data-table
                        :headers="webRequestHeaders"
                        :items="webRequests"
                        :search="search"
                        :options.sync="options"
                        :loading="loading"
                        :server-items-length="totalRows"
                        item-key="web_txn_id"
                        single-select
                        @click:row="handleWebTxnTableClick">
                            <template v-slot:item.start_time="{ item }">
                                {{ toHHMMSSFormat(item.start_time) }}
                            </template>
                            <template v-slot:item.ttl_txn_time="{ item }">
                                <BarWithContrast
                                    :mainValue="item.ttl_txn_time"
                                    :contrastValue="item.contrast_ttl_txn_time"
                                    :relativeValue="webRequests.maxValueInAttributes('ttl_txn_time', 'contrast_ttl_txn_time')"
                                    v-bind:contrastPeriodCode="getContrastTimeSelection"
                                    unit="ms"
                                    chartSize="small"
                                />
                            </template>
                            <template v-slot:item.ttl_bc_time="{ item }">
                                <BarWithContrast
                                    :mainValue="item.ttl_bc_time"
                                    :contrastValue="item.contrast_ttl_bc_time"
                                    :relativeValue="webRequests.maxValueInAttributes('ttl_bc_time', 'contrast_ttl_bc_time')"
                                    v-bind:contrastPeriodCode="getContrastTimeSelection"
                                    unit="ms"
                                    chartSize="small"
                                />
                            </template>
                            <template v-slot:item.num_of_bc_errors="{ item }">
                                {{ item.num_of_bc_errors }}
                                <v-icon v-if="item.num_of_bc_errors == 0" size="24" color="green">mdi-checkbox-marked-circle</v-icon>
                                <v-icon v-else size="24" color="red">mdi-alert-circle</v-icon>
                            </template>
                    </v-data-table>
                </v-card>
            </v-col>
        </v-row>

    </div>
</template>

<script>
    import { mapGetters, mapActions } from 'vuex'

    import Api from '@/services/api'
    import TimeHelper from '@/helpers/time_helper'

    import AutoRefresh from '@/components/AutoRefresh.vue'
    import TimePeriodSelection from '@/components/TimePeriodSelection.vue'
    import WebAppSelection from '@/components/WebAppSelection.vue'
    import BarWithContrast from '@/components/BarWithContrast.vue'
    import BlockchainRequestView from '@/components/BlockchainRequestView.vue'
    import WebRequestView from '@/components/WebRequestView.vue'
    import Drawer from '@/components/Drawer.vue'

    export default {

        components: {
            BarWithContrast, TimePeriodSelection, AutoRefresh, BlockchainRequestView, WebRequestView, Drawer, WebAppSelection
        },

        mounted: function () {
            this.setDefaultTimePeriod();
            this.getTransactionList();
        },

        watch: {
            options: {
                handler() {
                    if (!this.loading) {
                        this.getTransactionList()
                    }
                },
                deep: true,
            },
            currentWebAppName: function (newWebAppName, oldWebAppName) {
                this.getTransactionList();
            }
        },

        computed: {
            ...mapGetters([
                'currentWebAppName',
                'webAppNames',
                'getTimeSelection',
                'getContrastTimeSelection',
                'selectedTimePeriodStart',
                'selectedTimePeriodEnd',
                'contrastTimePeriodStart',
                'contrastTimePeriodEnd',
                'getFriendlySelectedTimePeriodStart',
                'getFriendlySelectedTimePeriodEnd'    
            ]),

        },

        methods: {

            ...TimeHelper,

            ...mapActions([
                'setTimeSelection',
                'setContrastTimeSelection',
            ]),      

            onSearchChange: function() {
                if (this.search == '') {
                    this.getTransactionList();
                }
            },

            onClearSearch: function() {
                this.search = '';
                this.getTransactionList();
            },

            onSearch: function(event) {
                this.options.page = 1;
                this.getTransactionList();
            },

            setDefaultTimePeriod: function() {
                this.setTimeSelection(this.getTimeSelection);
                this.setContrastTimeSelection(this.getContrastTimeSelection);
            },

            onBlockchainTxnSelected: function(item) {
                this.blockchainRequestViewDrawerStatus = 'expanded'
                this.selectedBlockchainTxn = item
            },

            handleWebTxnTableClick: function(item, row) {
                console.log("Selected web request:", item);
                row.select(true);                
                this.webRequestViewDrawerStatus = 'expanded'
                this.selectedWebTxn = item
            },

            getTransactionList: function() {
                this.loading = true
                Api.webRequests.getTransactionList(
                    {
                        ...this.options,
                        search: this.search,
                        time_period_start: this.selectedTimePeriodStart,
                        time_period_end: this.selectedTimePeriodEnd,
                        app_name: this.currentWebAppName
                    }                    
                )
                .then((response1) => {
                    this.loading = false
                    console.log("Web request list", response1.data);
                    let txns = response1.data
                    this.webRequests = txns           
                    this.totalRows = this.webRequests.length > 0 ? parseInt(this.webRequests[0].total) : 0
                    let allURIs = txns.map((txn) => { return txn.uri }).uniqueOnly() 
                    for (const uri of allURIs) {
                        Api.webRequests.getVitalDataByURI(
                            {
                                time_period_start: this.contrastTimePeriodStart,
                                time_period_end: this.contrastTimePeriodEnd,
                                app_name: this.currentWebAppName,
                                uri: uri
                            }                    
                        )
                        .then(response2 => {
                            console.log("Vital data by URI %s", uri, response2.data)
                            for (let index = 0; index < this.webRequests.length ; index++) {
                                if (uri == this.webRequests[index].uri) {
                                    let currentData = this.webRequests[index]
                                    currentData.contrast_ttl_txn_time = response2.data.avg_txn_time
                                    currentData.contrast_ttl_bc_time = response2.data.avg_bc_time
                                    this.$set(this.webRequests, index, currentData)
                                }
                            }
                        })
                    }
                })
            },

            onTimePeriodSelected: function(numOfMinutesAgo) {
                this.setTimeSelection(numOfMinutesAgo);
                this.getTransactionList();
            },

            onContrastTimePeriodSelected: function(numOfMinutesAgo) {
                this.setContrastTimeSelection(numOfMinutesAgo);
                this.getTransactionList();
            },

            onRefresh: function() {
                this.setTimeSelection(this.getTimeSelection);
                this.setContrastTimeSelection(this.getContrastTimeSelection);
                this.getTransactionList();
            },

        },

        data: () =>  ({
            selectedWebTxn: null,
            selectedBlockchainTxn: null,
            webRequestViewDrawerStatus: 'collapsed',
            blockchainRequestViewDrawerStatus: 'collapsed',
            search: '',
            options: {
                sortBy: ['start_time'],
                sortDesc: [true],
                mustSort: true
            },
            loading: false,
            totalRows: 0,
            webRequestHeaders: [
                {
                    text: 'Transaction name',
                    align: 'start',
                    sortable: false,
                    value: 'url',
                },
                {
                    text: 'Request location',
                    align: 'start',
                    value: 'remote_geolocation',
                    width: 260
                },
                { text: 'Start time', value: 'start_time', width: 110 },
                { text: 'Total transaction time', value: 'ttl_txn_time', width: 360 },
                { text: 'Time spent in blockchain', value: 'ttl_bc_time', width: 360 },
                { text: 'No. of blockchain calls', value: 'num_of_bc_calls' },
                { text: 'Blockchain errors', value: 'num_of_bc_errors'}
            ],
            webRequests: [],
        })
    }
</script>

<style scoped lang="scss">
</style>


